import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { confirmShifts, jobShiftsQuery, publishShifts, type Shift } from '../api/crew.ts';
import type { Job } from '../api/jobs.ts';
import { jobOffersQuery } from '../api/offers.ts';
import { jobTasksQuery } from '../api/tasks.ts';
import { Button } from '../components/button.tsx';
import { rosterRange } from '../roster-layout.ts';
import { RequiredTickets } from './crew-compliance.tsx';
import { stateWord } from './roster-bars.tsx';
import { Lead } from './tasks-panel.tsx';

// CB-REQ OPS-03 and CRW-12 at v1.998, CB-CONV §12 crew part 4B (4B-3): the job page's crew, slimmed
// to a summary — its tasks n of m, its shifts by state, its lead, the extra tickets it needs, and
// Open in roster, where the rows, forms and offers now live. Publish and Confirm stay here, as the
// job's own batch (CRW-12, CRW-16).

const ORDER = ['Draft', 'Published', 'Offered', 'Accepted', 'Confirmed', 'Declined'];

export function CrewSummary({
  job,
  mayManage,
  mayLead,
}: {
  job: Job;
  /** shifts.manage: publish and confirm, and the extra tickets. */
  mayManage: boolean;
  /** jobs.manage: the job's lead. */
  mayLead: boolean;
}) {
  const tasks = useQuery(jobTasksQuery(job.id));
  const shifts = useQuery(jobShiftsQuery(job.id));
  const offers = useQuery(jobOffersQuery(job.id));
  const queryClient = useQueryClient();
  const cancelled = job.status === 'cancelled';
  const editable = mayManage && !cancelled;
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['jobs', job.id] });
    await queryClient.invalidateQueries({ queryKey: ['shifts'] });
    await queryClient.invalidateQueries({ queryKey: ['roster'] });
  };

  const live = (tasks.data ?? []).filter((t) => t.status !== 'not_needed');
  const setAside = (tasks.data ?? []).length - live.length;
  const full = live.filter(
    (t) => t.status !== 'open' || t.fill === 'full' || t.fill === 'over',
  ).length;
  const part = live.filter((t) => t.status === 'open' && t.fill === 'part').length;

  const booked = (shifts.data ?? []).filter((s) => s.state !== 'cancelled');
  const byState = countBy(booked, stateWord);
  const untasked = booked.filter((s) => s.untasked).length;
  const drafts = booked.filter((s) => s.state === 'draft');
  // CRW-16 at v1.978: accepted shifts the office has not yet confirmed, and can.
  const unconfirmed = booked.filter(
    (s) => s.state === 'accepted' && !s.confirmedAt && s.missingToConfirm.length === 0,
  );
  const open = (offers.data ?? []).filter((o) => o.open).length;
  const publish = useMutation({
    mutationFn: () =>
      publishShifts(
        job.id,
        drafts.map((s) => s.id),
      ),
    onSuccess: refresh,
  });
  const confirm = useMutation({
    mutationFn: () => confirmShifts(unconfirmed.map((s) => s.id)),
    onSuccess: refresh,
  });
  const failed = publish.error ?? confirm.error;

  // The crew's span: bump-in to bump-out, else the sold times.
  const span = job.eventWindows
    ? rosterRange(job.eventWindows.bumpIn.localStart, job.eventWindows.bumpOut.localEnd)
    : rosterRange(job.soldWindow.localStart, job.soldWindow.localEnd);

  return (
    <section className="flex flex-col gap-4" aria-labelledby="crew">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 id="crew" className="section-title flex-1">
          Crew
        </h2>
        {editable && drafts.length > 0 ? (
          <Button variant="quiet" disabled={publish.isPending} onClick={() => publish.mutate()}>
            Publish ({drafts.length})
          </Button>
        ) : null}
        {editable && unconfirmed.length > 0 ? (
          <Button variant="quiet" disabled={confirm.isPending} onClick={() => confirm.mutate()}>
            Confirm ({unconfirmed.length})
          </Button>
        ) : null}
        <Link
          to="/roster"
          search={{ from: span.from, days: span.days, job: job.id }}
          className="link text-sm font-semibold"
        >
          Open in roster
        </Link>
      </div>
      {failed ? (
        <p role="alert" className="alert">
          {failed.message}
        </p>
      ) : null}
      {cancelled && booked.length > 0 ? (
        <p role="alert" className="alert">
          This job was cancelled, but{' '}
          {booked.length === 1
            ? 'its shift still stands'
            : `its ${booked.length} shifts still stand`}{' '}
          — anyone who said yes still thinks they’re working. Tell each person, then rebook, record
          they pulled out, or cancel each in the roster.
        </p>
      ) : null}
      <Lead job={job} tasks={live} editable={mayLead && !cancelled} />
      {tasks.isError || shifts.isError ? (
        <p role="alert" className="alert">
          {(tasks.error ?? shifts.error)!.message}
        </p>
      ) : null}
      <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[8rem_1fr]">
        <dt className="text-ink-soft">Tasks</dt>
        <dd>
          {tasks.data
            ? live.length === 0
              ? 'None'
              : [
                  `${full} of ${live.length} full`,
                  part ? `${part} part-filled` : '',
                  setAside ? `${setAside} not needed` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')
            : '…'}
        </dd>
        <dt className="text-ink-soft">Shifts</dt>
        <dd>
          {shifts.data
            ? booked.length === 0
              ? 'None'
              : [
                  ...ORDER.filter((w) => byState.has(w)).map(
                    (w) => `${byState.get(w)} ${w.toLowerCase()}`,
                  ),
                  untasked ? `${untasked} with no task` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')
            : '…'}
        </dd>
        {open > 0 ? (
          <>
            <dt className="text-ink-soft">Offers</dt>
            <dd>{open} open</dd>
          </>
        ) : null}
      </dl>
      {/* CMP-07 at v1.965: extra tickets this show needs, on top of the venue's and each role's. */}
      <RequiredTickets
        owner="jobs"
        id={job.id}
        label="Extra tickets for this job"
        editable={editable}
      />
    </section>
  );
}

function countBy(shifts: readonly Shift[], key: (s: Shift) => string): Map<string, number> {
  const out = new Map<string, number>();
  for (const s of shifts) out.set(key(s), (out.get(key(s)) ?? 0) + 1);
  return out;
}
